import { Injectable } from '@angular/core';
import { AuthState, Logout } from '@core/states/auth.state';
import { Store } from '@ngxs/store';
import { jwtDecode } from 'jwt-decode';

/**
 * @title JwtService
 * @since 1.0.0
 * 
 * @description This service is responsible for validating JWT tokens.
 * It checks if the token is present and not expired. If the token is invalid or expired,
 * it dispatches a logout action to clear the authentication state.
 * 
 */
@Injectable({
	providedIn: 'root'
})
export class JwtService {

	/**
	 * @param {Store} store - The NGXS store for managing application state.
	 * @since 1.0.0
	 */
	constructor(
		private store: Store
	) { }

	/**
	 * Validates the JWT token.
	 * @returns {boolean} - Returns true if the token is valid, false otherwise.
	 * 
	 * This method checks if the token is present in the store and verifies its expiration.
	 * If the token is invalid or expired, it dispatches a logout action to clear the authentication state.
	 */
	validate(): boolean {
		const token = this.store.selectSnapshot(AuthState.getToken);
		if (!token || this.isTokenExpired(token)) {
			this.store.dispatch(new Logout());
			return false;
		}
		return true;
	}

	/**
	 * Checks if the token is expired.
	 * @param {string} token - The JWT token to check.
	 * @returns {boolean} - Returns true if the token is expired, false otherwise.
	 * 
	 * This method decodes the token and compares the expiration time with the current time.
	 */
	private isTokenExpired(token: string): boolean {
		try {
			const decoded: { exp: number } = jwtDecode(token);
			const currentTime = Math.floor(Date.now() / 1000);
			return decoded.exp < currentTime;
		} catch {
			return true;
		}
	}
} 
